/**
 * PersonaRoutingDecisionCard — typed detail card for a single
 * persona-routing AGENT_DECISION episode.
 *
 * The cosine + jaccard scorer used to do this routing silently — there
 * was nothing to inspect. The LLM ranker emits a structured verdict
 * (rankings array, per-persona reasoning, concept overlaps,
 * overall_reasoning, model + latency) that this card renders verbatim
 * so admins can debug "why was persona X dropped from creative Y?"
 * without re-running the pipeline.
 *
 * Used by:
 *   - /creative-memory/routing (cross-playbook list view)
 *   - PlaybookDetailPage → Persona Routing tab (inline list)
 *   - /creative-memory/decisions (drawer detail when decision_type =
 *     "persona_routing")
 */

import { clsx } from 'clsx';
import {
  CheckCircle2,
  XCircle,
  Route,
  Clock,
  Cpu,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import type {
  CreativeMemoryActivityItem,
  PersonaRanking,
} from '@/services/api';

interface PersonaRoutingDecisionCardProps {
  decision: CreativeMemoryActivityItem;
  /** Open by default — useful when the card is the only thing on screen. */
  defaultExpanded?: boolean;
}

const relevanceClass = (relevance: PersonaRanking['relevance']): string => {
  switch (relevance) {
    case 'primary':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'secondary':
      return 'bg-sky-100 text-sky-800 border-sky-200';
    case 'low':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'skip':
      return 'bg-rose-50 text-rose-700 border-rose-200';
  }
};

const formatRelative = (iso?: string): string => {
  if (!iso) return '—';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s ago`;
    if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`;
    if (diffMs < 86_400_000) return `${Math.round(diffMs / 3_600_000)}h ago`;
    return `${Math.round(diffMs / 86_400_000)}d ago`;
  } catch {
    return iso.slice(0, 10);
  }
};

/** Render "<id> · <name>" when a name is known, "<id>" otherwise. */
function ConceptChip({
  conceptId,
  conceptName,
}: {
  conceptId: string;
  conceptName?: string;
}) {
  if (conceptName && conceptName !== conceptId) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-700">
        <span className="text-slate-800">{conceptName}</span>
        <span className="text-slate-400 font-mono">·</span>
        <span className="text-slate-400 font-mono">{conceptId}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-mono text-slate-600">
      {conceptId}
    </span>
  );
}

export function PersonaRoutingDecisionCard({
  decision,
  defaultExpanded = false,
}: PersonaRoutingDecisionCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const rankings = decision.rankings || [];
  const selected = new Set(decision.selected_persona_ids || []);
  const conceptNames = decision.concept_names || {};

  // Surface a compact "selected vs dropped" header so the user can
  // scan a long list without expanding every card.
  const selectedRankings = rankings.filter((r) => selected.has(r.persona_id));
  const droppedRankings = rankings.filter((r) => !selected.has(r.persona_id));

  return (
    <article className="rounded-lg border border-slate-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50/60"
      >
        <div className="min-w-0 flex-1">
          <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              <Route className="w-3 h-3" />
              persona routing
            </span>
            {decision.creative_id && (
              <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-mono text-sky-700">
                creative {decision.creative_id}
              </span>
            )}
            {decision.playbook_id && (
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-mono text-amber-700">
                pb {decision.playbook_id}
              </span>
            )}
            {decision.client_uid && (
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-mono text-emerald-700">
                {decision.client_uid}
              </span>
            )}
            <span className="ml-auto text-[10px] text-slate-400">
              {formatRelative(decision.ts)}
            </span>
          </header>

          <p className="mt-2 text-xs text-slate-700">
            <span className="font-medium text-slate-800">Selected:</span>{' '}
            {selectedRankings.length === 0 && (
              <span className="text-slate-500 italic">none</span>
            )}
            {selectedRankings.map((r, idx) => (
              <span key={r.persona_id}>
                {idx > 0 && ', '}
                <span className="font-mono text-emerald-700">{r.persona_id}</span>
                <span className="text-slate-400"> ({r.score.toFixed(2)})</span>
              </span>
            ))}
            {droppedRankings.length > 0 && (
              <>
                <span className="mx-1 text-slate-400">·</span>
                <span className="text-slate-500">
                  dropped {droppedRankings.length}
                </span>
              </>
            )}
          </p>

          <footer className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
            {decision.ranker_model && (
              <span className="inline-flex items-center gap-1">
                <Cpu className="w-3 h-3" />
                {decision.ranker_model}
              </span>
            )}
            {typeof decision.ranker_elapsed_ms === 'number' && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {decision.ranker_elapsed_ms}ms
              </span>
            )}
            {decision.pdo_concepts_consulted.length > 0 && (
              <span>{decision.pdo_concepts_consulted.length} concepts consulted</span>
            )}
          </footer>
        </div>
        <ChevronDown
          className={clsx(
            'mt-1 w-4 h-4 text-slate-400 transition-transform shrink-0',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-4">
          {decision.overall_reasoning && (
            <section>
              <h4 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5 text-violet-500" />
                Overall reasoning
              </h4>
              <p className="text-[12px] text-slate-700 leading-snug">
                {decision.overall_reasoning}
              </p>
            </section>
          )}

          {rankings.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
                Per-persona ranking
              </h4>
              <ol className="space-y-2">
                {rankings.map((r) => {
                  const isSelected = selected.has(r.persona_id);
                  return (
                    <li
                      key={r.persona_id}
                      className={clsx(
                        'rounded border px-3 py-2',
                        isSelected
                          ? 'border-emerald-200 bg-emerald-50/40'
                          : 'border-slate-200 bg-white',
                      )}
                    >
                      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-[10px] font-mono text-slate-400">
                          #{r.rank}
                        </span>
                        <span className="text-[12px] font-mono font-medium text-slate-800">
                          {r.persona_id}
                        </span>
                        {isSelected ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-slate-300" />
                        )}
                        <span
                          className={clsx(
                            'rounded-full border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide',
                            relevanceClass(r.relevance),
                          )}
                        >
                          {r.relevance}
                        </span>
                        <span className="ml-auto text-[11px] font-mono tabular-nums text-slate-600">
                          {r.score.toFixed(2)}
                        </span>
                      </header>
                      {r.reasoning && (
                        <p className="mt-1 text-[12px] text-slate-700 leading-snug">
                          {r.reasoning}
                        </p>
                      )}
                      {r.concept_overlaps.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {r.concept_overlaps.map((cid) => (
                            <ConceptChip
                              key={cid}
                              conceptId={cid}
                              conceptName={conceptNames[cid]}
                            />
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </div>
      )}
    </article>
  );
}
